"use client";

import { Transition } from "@headlessui/react";
import {
  InboxIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";

export default function AlertNotification({
  show = false,
  title = "Notification",
  message = "",
  type = "error",
  actions = [],
  onClose = () => {},
}) {
  // Icon mapping based on type
  const iconMap = {
    error: (
      <ExclamationCircleIcon
        aria-hidden="true"
        className="size-6 text-red-500"
      />
    ),
    success: (
      <CheckCircleIcon aria-hidden="true" className="size-6 text-green-500" />
    ),
    info: (
      <InformationCircleIcon
        aria-hidden="true"
        className="size-6 text-indigo-500"
      />
    ),
    default: <InboxIcon aria-hidden="true" className="size-6 text-gray-400" />,
  };

  // Color mapping for the title based on type
  const titleColorMap = {
    error: "text-red-700",
    success: "text-green-700",
    info: "text-indigo-700",
    default: "text-gray-900",
  };

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed right-0 top-0 inset-0 px-4 py-6 z-50"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition show={show}>
            <div className="pointer-events-auto w-96 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition data-closed:data-enter:translate-y-2 data-enter:transform data-closed:opacity-0 data-enter:duration-300 data-leave:duration-100 data-enter:ease-out data-leave:ease-in sm:data-closed:data-enter:translate-x-2 sm:data-closed:data-enter:translate-y-0">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    {iconMap[type] || iconMap.default}
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p
                      className={`text-sm font-medium ${titleColorMap[type] || titleColorMap.default}`}
                    >
                      {title}
                    </p>
                    <p
                      className="mt-1 text-sm text-gray-500"
                      dangerouslySetInnerHTML={{ __html: message }}
                    />
                    {actions.length > 0 && (
                      <div className="mt-3 flex space-x-7">
                        {actions.map((action, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={action.onClick}
                            className={`rounded-md bg-white text-sm font-medium ${
                              index === 0
                                ? "text-indigo-600 hover:text-indigo-500"
                                : "text-gray-700 hover:text-gray-500"
                            } focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon aria-hidden="true" className="size-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
